import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  AdminEventType,
  ADMIN_EVENT_TYPE_LABELS,
  ADMIN_EVENT_TYPE_COLORS,
  AdminEvent,
  CreateAdminEventInput,
  UpdateAdminEventInput,
} from '../../types/adminCalendar';
import { useThemeStore } from '../../stores/themeStore';

interface AdminAddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateAdminEventInput) => void;
  onUpdate?: (id: string, input: UpdateAdminEventInput) => void;
  onDelete?: (id: string) => void;
  editingEvent?: AdminEvent | null;
  defaultDate?: string;
}

const EVENT_TYPES: AdminEventType[] = ['api_payment', 'tax', 'subscription', 'maintenance', 'meeting', 'other'];

const AdminAddEventModal: React.FC<AdminAddEventModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  editingEvent,
  defaultDate,
}) => {
  const isDark = useThemeStore((s) => s.theme) === 'dark';
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState<AdminEventType>('api_payment');
  const [eventDate, setEventDate] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (editingEvent) {
      setTitle(editingEvent.title);
      setEventType(editingEvent.event_type);
      setEventDate(editingEvent.event_date.slice(0, 16));
      setDescription(editingEvent.description || '');
    } else {
      setTitle('');
      setEventType('api_payment');
      setEventDate(defaultDate || '');
      setDescription('');
    }
  }, [editingEvent, isOpen, defaultDate]);

  if (!isOpen) return null;

  const canSave = title.trim() && eventDate;

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      title: title.trim(),
      event_type: eventType,
      event_date: new Date(eventDate).toISOString(),
      description: description.trim() || undefined,
    };
    if (editingEvent && onUpdate) {
      onUpdate(editingEvent.id, payload);
    } else {
      onCreate(payload);
    }
    onClose();
  };

  const handleDelete = () => {
    if (editingEvent && onDelete) {
      onDelete(editingEvent.id);
      onClose();
    }
  };

  const inputCls = `w-full px-3 py-2.5 rounded-xl text-sm transition-colors focus:outline-none ${
    isDark
      ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-cyan-500/50'
      : 'bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-cyan-400'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${
        isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
          <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </h3>
          <button onClick={onClose} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. OpenAI API billing" className={inputCls} />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Type</label>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setEventType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border flex items-center gap-1.5 ${
                    eventType === t
                      ? isDark
                        ? 'border-cyan-500/50 bg-cyan-500/10 text-white'
                        : 'border-cyan-400 bg-cyan-50 text-slate-800'
                      : isDark
                        ? 'border-white/10 bg-white/5 text-slate-400 hover:bg-white/10'
                        : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ADMIN_EVENT_TYPE_COLORS[t] }} />
                  {ADMIN_EVENT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Date & Time</label>
            <input type="datetime-local" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className={inputCls} />
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Notes (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any extra details..."
              rows={2}
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-5 py-4 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
          <div>
            {editingEvent && onDelete && (
              <button onClick={handleDelete} className="px-3 py-2 rounded-lg text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors">
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors ${isDark ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-100'}`}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={`px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                canSave
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:opacity-90'
                  : isDark ? 'bg-white/5 text-slate-600 cursor-not-allowed' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {editingEvent ? 'Save Changes' : 'Add Event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAddEventModal;
